import { atom } from "jotai"
import api from "../services/api"
import { loadingAtom, userAtom, lastPlanTokenAtom } from "./index"
import { generatePlanLinks } from "../helpers"

// Plans state atoms
export const plansAtom = atom([])

// Search term atom
const searchTermAtom = atom('')

// Derived atom for filtered plans with search functionality
export const filteredPlansAtom = atom(
  (get) => {
    const plans = get(plansAtom)
    const searchTerm = get(searchTermAtom)
    if (!searchTerm) {
      return plans
    }
    return plans.filter(plan => plan.name?.toLowerCase().includes(searchTerm.toLowerCase()))
  },
  (get, set, searchTerm) => {
    set(searchTermAtom, searchTerm)
  }
)

// Atom action for fetching plans list
export const fetchPlansAtom = atom(
  null,
  async (get, set, { token, navigate, setJoinOpen, setNoPlans } = {}) => {
    set(loadingAtom, true)

    try {
      const response = await api.getPlans()
      set(plansAtom, response.data)

      // Handle navigation and UI state based on plans data
      // Only redirect if there are no plans AND we're not viewing a specific plan
      if (response.data.length === 0 && !token) {
        const user = get(userAtom)

        // Admin users navigate to different routes based on organization_id
        if (user?.is_admin) {
          navigate?.('/plan/no-plans')
        } else {
          navigate?.('/plan/no-plans')
        }

        setNoPlans?.(true)
      }

      if (token && !response.data.find(plan => plan.token === token)) {
        setJoinOpen?.(true)
      }

      if (!token && response.data.length > 0) {
        // Check if there's a saved last plan token
        const lastToken = get(lastPlanTokenAtom)
        let targetPlan = response.data[0] // Default to first plan

        // If lastToken exists and is in user's plans, use it instead
        if (lastToken) {
          const savedPlan = response.data.find(plan => plan.token === lastToken)
          if (savedPlan) {
            targetPlan = savedPlan
          } else {
            // Clear invalid token
            set(lastPlanTokenAtom, null)
          }
        }

        const links = generatePlanLinks(targetPlan)
        const defaultPath = links[0]?.path ?? `/plan/${targetPlan.token}/activities`
        navigate?.(defaultPath)
      }

      return response.data
    } catch (error) {
      console.error('Error fetching plans:', error)
      throw error
    } finally {
      set(loadingAtom, false)
    }
  }
)

export const refetchPlansAtom = atom(
  null,
  async (get, set) => {
    try {
      const response = await api.getPlans()
      set(plansAtom, response.data)
    } catch (error) {
      console.error('Error fetching plans:', error)
      throw error
    }
  }
)
